/**
 * Celebration - Visual Effects System
 * 
 * Dramatic casino-inspired celebration effects for T-Junctions
 * and other scoring events. Uses canvas-based particle system.
 */

/** Particle definition */
interface Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    size: number;
    color: string;
    alpha: number;
    rotation: number;
    rotationSpeed: number;
    life: number;
    maxLife: number;
    type: 'coin' | 'spark' | 'star' | 'confetti';
}

/** Text popup definition */
interface TextPopup {
    x: number;
    y: number;
    text: string;
    color: string;
    size: number;
    alpha: number;
    life: number;
    maxLife: number;
    vy: number;
    scale: number;
}

/** Celebration colors */
const COLORS = {
    gold: ['#FFD700', '#FFA500', '#FFB347', '#FFCC00'] as const,
    rainbow: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'] as const,
    fire: ['#FF4500', '#FF6347', '#FF7F50', '#FFA500'] as const,
    electric: ['#00FFFF', '#00CED1', '#20B2AA', '#48D1CC'] as const,
};

/**
 * Pick a random color from an array (with type safety)
 */
function pickColor(colors: readonly string[]): string {
    const index = Math.floor(Math.random() * colors.length);
    return colors[index] ?? colors[0] ?? '#FFFFFF';
}

/**
 * Celebration class - manages visual effects
 */
export class Celebration {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private particles: Particle[] = [];
    private textPopups: TextPopup[] = [];
    private animationId: number | null = null;
    private isRunning = false;
    
    // Combo tracking
    private comboCount = 0;
    private lastTJunctionTime = 0;
    private readonly comboWindow = 3000; // 3 seconds to maintain combo

    constructor(parentElement: HTMLElement) {
        // Create overlay canvas
        this.canvas = document.createElement('canvas');
        this.canvas.id = 'celebration-canvas';
        this.canvas.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 150;
        `;
        parentElement.appendChild(this.canvas);
        
        const ctx = this.canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Failed to get 2D context');
        }
        this.ctx = ctx;
        
        // Handle resize
        this.resize();
        window.addEventListener('resize', () => this.resize());
    }

    /**
     * Resize canvas to window
     */
    private resize(): void {
        this.canvas.width = window.innerWidth;
        this.canvas.height = window.innerHeight;
    }

    /**
     * Trigger T-Junction celebration
     */
    tJunction(x: number, y: number, points: number): void {
        const now = performance.now();
        
        // Update combo
        if (now - this.lastTJunctionTime < this.comboWindow) {
            this.comboCount++;
        } else {
            this.comboCount = 1;
        }
        this.lastTJunctionTime = now;
        
        // Intensity based on combo
        const intensity = Math.min(this.comboCount, 5);
        
        // Spawn coin burst
        this.spawnCoinBurst(x, y, 10 + intensity * 5);
        
        // Spawn sparks
        this.spawnSparks(x, y, 8 + intensity * 3);
        
        // Text popup
        let text = `+${points}`;
        let color = '#FFD700';
        let size = 32;
        
        if (this.comboCount > 1) {
            text = `${this.comboCount}x COMBO!\n+${points}`;
            color = this.comboCount >= 3 ? '#FF6B6B' : '#00FF88';
            size = 36 + this.comboCount * 2;
        }
        
        this.spawnTextPopup(x, y - 30, text, color, size);
        
        // Screen shake for high combos
        if (this.comboCount >= 3) {
            this.screenShake(3 + this.comboCount);
        }
        
        // Start animation if not running
        this.startAnimation();
    }

    /**
     * Spawn a burst of coins
     */
    private spawnCoinBurst(x: number, y: number, count: number): void {
        const colors = COLORS.gold;
        
        for (let i = 0; i < count; i++) {
            const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
            const speed = 150 + Math.random() * 200;
            
            this.particles.push({
                x,
                y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 100, // Bias upward
                size: 8 + Math.random() * 6,
                color: pickColor(colors),
                alpha: 1,
                rotation: Math.random() * Math.PI * 2,
                rotationSpeed: (Math.random() - 0.5) * 10,
                life: 1,
                maxLife: 1,
                type: 'coin',
            });
        }
    }

    /**
     * Spawn sparks
     */
    private spawnSparks(x: number, y: number, count: number): void {
        const colors = COLORS.electric;
        
        for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 200 + Math.random() * 300;
            
            this.particles.push({
                x,
                y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                size: 2 + Math.random() * 3,
                color: pickColor(colors),
                alpha: 1,
                rotation: 0,
                rotationSpeed: 0,
                life: 1,
                maxLife: 1,
                type: 'spark',
            });
        }
    }

    /**
     * Spawn star particles
     */
    spawnStars(x: number, y: number, count: number): void {
        const colors = COLORS.rainbow;
        
        for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 100 + Math.random() * 150;
            
            this.particles.push({
                x,
                y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed - 50,
                size: 10 + Math.random() * 10,
                color: pickColor(colors),
                alpha: 1,
                rotation: Math.random() * Math.PI * 2,
                rotationSpeed: (Math.random() - 0.5) * 5,
                life: 1,
                maxLife: 1,
                type: 'star',
            });
        }
        
        this.startAnimation();
    }

    /**
     * Spawn confetti burst
     */
    spawnConfetti(x: number, y: number, count: number): void {
        const colors = COLORS.rainbow;
        
        for (let i = 0; i < count; i++) {
            const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI; // Upward cone
            const speed = 200 + Math.random() * 300;
            
            this.particles.push({
                x,
                y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                size: 6 + Math.random() * 4,
                color: pickColor(colors),
                alpha: 1,
                rotation: Math.random() * Math.PI * 2,
                rotationSpeed: (Math.random() - 0.5) * 15,
                life: 1.5,
                maxLife: 1.5,
                type: 'confetti',
            });
        }
        
        this.startAnimation();
    }

    /**
     * Spawn text popup
     */
    private spawnTextPopup(x: number, y: number, text: string, color: string, size: number): void {
        this.textPopups.push({
            x,
            y,
            text,
            color,
            size,
            alpha: 1,
            life: 1.5,
            maxLife: 1.5,
            vy: -60,
            scale: 0,
        });
    }

    /**
     * Screen shake effect
     */
    private screenShake(intensity: number): void {
        const duration = 200;
        const startTime = performance.now();
        
        const shake = (): void => {
            const elapsed = performance.now() - startTime;
            const progress = elapsed / duration;
            
            if (progress < 1) {
                const decay = 1 - progress;
                const offsetX = (Math.random() - 0.5) * intensity * decay * 2;
                const offsetY = (Math.random() - 0.5) * intensity * decay * 2;
                
                document.body.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
                requestAnimationFrame(shake);
            } else {
                document.body.style.transform = '';
            }
        };
        
        requestAnimationFrame(shake);
    }

    /**
     * Start the animation loop
     */
    private startAnimation(): void {
        if (this.isRunning) return;
        
        this.isRunning = true;
        let lastTime = performance.now();
        
        const animate = (currentTime: number): void => {
            const dt = (currentTime - lastTime) / 1000;
            lastTime = currentTime;
            
            this.update(dt);
            this.render();
            
            // Continue if there are particles or popups
            if (this.particles.length > 0 || this.textPopups.length > 0) {
                this.animationId = requestAnimationFrame(animate);
            } else {
                this.isRunning = false;
                this.animationId = null;
            }
        };
        
        this.animationId = requestAnimationFrame(animate);
    }

    /**
     * Update particles and popups
     */
    private update(dt: number): void {
        const gravity = 400;
        
        // Update particles
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];
            if (!p) continue;
            
            // Physics
            p.x += p.vx * dt;
            p.vy += gravity * dt;
            p.y += p.vy * dt;
            p.rotation += p.rotationSpeed * dt;
            
            // Drag
            p.vx *= 0.99;
            
            // Life
            p.life -= dt;
            p.alpha = Math.max(0, p.life / p.maxLife);
            
            // Remove dead particles
            if (p.life <= 0 || p.y > this.canvas.height + 50) {
                this.particles.splice(i, 1);
            }
        }
        
        // Update text popups
        for (let i = this.textPopups.length - 1; i >= 0; i--) {
            const t = this.textPopups[i];
            if (!t) continue;
            
            // Movement
            t.y += t.vy * dt;
            t.vy *= 0.95; // Slow down
            
            // Scale animation
            const lifeRatio = t.life / t.maxLife;
            if (lifeRatio > 0.9) {
                // Pop in
                t.scale = 1 - (lifeRatio - 0.9) * 10;
            } else if (lifeRatio < 0.2) {
                // Fade out
                t.scale = lifeRatio / 0.2;
                t.alpha = lifeRatio / 0.2;
            } else {
                t.scale = 1;
            }
            
            // Life
            t.life -= dt;
            
            // Remove dead popups
            if (t.life <= 0) {
                this.textPopups.splice(i, 1);
            }
        }
    }

    /**
     * Render particles and popups
     */
    private render(): void {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        
        // Render particles
        for (const p of this.particles) {
            this.ctx.save();
            this.ctx.globalAlpha = p.alpha;
            this.ctx.translate(p.x, p.y);
            this.ctx.rotate(p.rotation);
            
            switch (p.type) {
                case 'coin':
                    this.renderCoin(p);
                    break;
                case 'spark':
                    this.renderSpark(p);
                    break;
                case 'star':
                    this.renderStar(p);
                    break;
                case 'confetti':
                    this.renderConfetti(p);
                    break;
            }
            
            this.ctx.restore();
        }
        
        // Render text popups
        for (const t of this.textPopups) {
            this.ctx.save();
            this.ctx.globalAlpha = t.alpha;
            this.ctx.translate(t.x, t.y);
            this.ctx.scale(t.scale, t.scale);
            
            // Text with outline
            this.ctx.font = `bold ${t.size}px 'Segoe UI', sans-serif`;
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'middle';
            
            // Shadow
            this.ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
            this.ctx.shadowBlur = 4;
            this.ctx.shadowOffsetY = 2;
            
            // Outline
            this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
            this.ctx.lineWidth = 4;
            
            // Handle multi-line text
            const lines = t.text.split('\n');
            const lineHeight = t.size * 1.2;
            const startY = -((lines.length - 1) * lineHeight) / 2;
            
            for (let i = 0; i < lines.length; i++) {
                const line = lines[i];
                if (!line) continue;
                const y = startY + i * lineHeight;
                this.ctx.strokeText(line, 0, y);
                this.ctx.fillStyle = t.color;
                this.ctx.fillText(line, 0, y);
            }
            
            this.ctx.restore();
        }
    }

    /**
     * Render a coin particle
     */
    private renderCoin(p: Particle): void {
        // Coin is an ellipse that rotates
        const squeeze = Math.abs(Math.cos(p.rotation * 2));
        
        this.ctx.fillStyle = p.color;
        this.ctx.beginPath();
        this.ctx.ellipse(0, 0, p.size * squeeze, p.size, 0, 0, Math.PI * 2);
        this.ctx.fill();
        
        // Highlight
        this.ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
        this.ctx.beginPath();
        this.ctx.ellipse(-p.size * 0.3 * squeeze, -p.size * 0.3, p.size * 0.3 * squeeze, p.size * 0.3, 0, 0, Math.PI * 2);
        this.ctx.fill();
    }

    /**
     * Render a spark particle
     */
    private renderSpark(p: Particle): void {
        // Spark is a small line
        const length = p.size * 3;
        const angle = Math.atan2(p.vy, p.vx);
        
        this.ctx.strokeStyle = p.color;
        this.ctx.lineWidth = p.size;
        this.ctx.lineCap = 'round';
        this.ctx.beginPath();
        this.ctx.moveTo(-Math.cos(angle) * length / 2, -Math.sin(angle) * length / 2);
        this.ctx.lineTo(Math.cos(angle) * length / 2, Math.sin(angle) * length / 2);
        this.ctx.stroke();
    }

    /**
     * Render a star particle
     */
    private renderStar(p: Particle): void {
        const spikes = 5;
        const outerRadius = p.size;
        const innerRadius = p.size * 0.4;
        
        this.ctx.fillStyle = p.color;
        this.ctx.beginPath();
        
        for (let i = 0; i < spikes * 2; i++) {
            const radius = i % 2 === 0 ? outerRadius : innerRadius;
            const angle = (i * Math.PI) / spikes - Math.PI / 2;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            
            if (i === 0) {
                this.ctx.moveTo(x, y);
            } else {
                this.ctx.lineTo(x, y);
            }
        }
        
        this.ctx.closePath();
        this.ctx.fill();
    }

    /**
     * Render a confetti particle
     */
    private renderConfetti(p: Particle): void {
        // Confetti is a small rectangle
        this.ctx.fillStyle = p.color;
        this.ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
    }

    /**
     * Reset combo counter
     */
    resetCombo(): void {
        this.comboCount = 0;
    }

    /**
     * Get current combo count
     */
    getComboCount(): number {
        return this.comboCount;
    }

    /**
     * Clean up
     */
    destroy(): void {
        if (this.animationId !== null) {
            cancelAnimationFrame(this.animationId);
        }
        this.canvas.remove();
    }
}
